import { DestroyRef, Directive, ElementRef, Input, OnInit, Renderer2, inject } from '@angular/core'
import {
    animationFrameScheduler,
    BehaviorSubject,
    combineLatest,
    switchMap,
    map,
    interval,
    takeWhile,
    endWith,
    distinctUntilChanged,
} from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

/**
 * Quadratic Ease-Out Function: f(x) = x * (2 - x)
 */
const easeOutQuad = (x: number): number => x * (2 - x)

@Directive({
    selector: '[countUp]',
    standalone: true,
})
export class CountUpDirective implements OnInit {
    private readonly elementRef = inject(ElementRef)
    private readonly renderer = inject(Renderer2)
    private destroyRef = inject(DestroyRef)

    private readonly count$ = new BehaviorSubject(0)
    private readonly duration$ = new BehaviorSubject(2000)

    private readonly currentCount$ = combineLatest([this.count$, this.duration$]).pipe(
        switchMap(([count, duration]) => {
            // get the time when animation is triggered
            const startTime = animationFrameScheduler.now()

            return interval(0, animationFrameScheduler).pipe(
                // calculate elapsed time
                map(() => animationFrameScheduler.now() - startTime),
                // calculate progress
                map((elapsedTime) => elapsedTime / duration),
                // complete when progress is greater than 1
                takeWhile((progress) => progress <= 1),
                // apply quadratic ease-out
                // for faster start and slower end of counting
                map(easeOutQuad),
                // calculate current count
                map((progress) => Math.round(progress * count)),
                // make sure that last emitted value is count
                endWith(count),
                distinctUntilChanged()
            )
        })
    )

    @Input('countUp')
    set count(count: number) {
        this.count$.next(count)
    }

    @Input()
    set duration(duration: number) {
        this.duration$.next(duration)
    }

    ngOnInit(): void {
        this.displayCurrentCount()
    }

    private displayCurrentCount(): void {
        this.currentCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((currentCount) => {
            this.renderer.setProperty(this.elementRef.nativeElement, 'innerHTML', currentCount)
        })
    }
}
